/* tempexp_18086-start */
import { Icon20CalendarOutline, Icon20InfoCircleOutline, Icon20UserOutline } from '@vkontakte/icons';
import { Spacing, Text } from '@vkontakte/vkui';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { getExpiredBonusDays, REFERRAL_ANALYTIC_NAME } from 'reactApp/appHelpers/featuresHelpers/features/referralProgram';
import { BillingDataReferralSelectors } from 'reactApp/modules/billingData/billingData.referral.selectors';
import type { BillingReferralProgram } from 'reactApp/modules/billingData/billingData.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { SubscriptionInfoBlock } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionInfo/SubscriptionInfoBlock/SubscriptionInfoBlock';
import Content from 'reactApp/ui/Content/Content';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { CommonInfo } from 'reactApp/ui/Mobile/SubscriptionInfoModal/Components/CommonInfo/CommonInfo';
import { renderReferralModal } from 'reactApp/ui/ReferralProgram/ReferralModal/ReferralModal.helpers';
import { formatDefaultDate } from 'reactApp/utils/formatDate';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import { FriendAvatar } from '../FriendAvatar/FriendAvatar';
import { BonusExpiresWarning } from './BonusExpiresWarning/BonusExpiresWarning';
import { FeaturesList } from './FeaturesList/FeaturesList';
import styles from './ReferralUserModal.css';
import { ReferralInfoItem } from './touch/InfoItem/ReferralInfoItem';

export interface ReferralUserModalProps {
    info: BillingReferralProgram;
    onClose?(): void;
}

enum InfoTypes {
    friendBonus = 'friendBonus',
    conditionBonus = 'conditionBonus',
    startBonus = 'startBonus',
    featuresBonus = 'featuresBonus',
}

enum InfoTitles {
    friendBonus = 'Начислен за друга',
    conditionBonus = 'Условие',
    startBonus = 'Бонус получен',
    featuresBonus = 'Возможности подписки',
}

export function ReferralUserModal({ onClose, info }: ReferralUserModalProps) {
    const { quota, start, expires, autorenewable } = info;
    const isMobile = useSelector(EnvironmentSelectors.isMobile);
    const expiredAfterDays = getExpiredBonusDays(expires, autorenewable);
    const referalState = expiredAfterDays ? 'passive' : 'active';
    const referralQuota = useSelector(BillingDataReferralSelectors.getReferralCommonQuota);
    const referralList = useSelector(BillingDataReferralSelectors.sortReferralsByExpires);
    useEffect(() => {
        emitAnalyticEvent(AnalyticEventNames.REFERRAL_BONUS_INFO_SHOW);
        sendPaymentGa({
            action: 'show',
            eventCategory: ECategoryGa.referalInfo,
            name: REFERRAL_ANALYTIC_NAME,
            referal_friend_id: info.owner.email,
            referal_state: referalState,
            date_referal_bonus_get: info.start,
        });
    }, []);
    const handleClose = useCallback(() => {
        emitAnalyticEvent(AnalyticEventNames.REFERRAL_BONUS_INFO_CLOSE);
        sendPaymentGa({
            action: 'close',
            eventCategory: ECategoryGa.referalInfo,
            name: REFERRAL_ANALYTIC_NAME,
            referal_friend_id: info.owner.email,
            referal_state: referalState,
            date_referal_bonus_get: info.start,
        });
        renderReferralModal({
            quota: referralQuota,
            list: referralList,
            isReturn: true,
        });
        onClose?.();
    }, [onClose, referralQuota, referralList]);
    const scrollRef = useRef<HTMLDivElement>(null);
    const description = 'Действует, пока подписка друга активна';
    const bonusStart = formatDefaultDate(start);

    const header = useMemo(() => {
        return <div className={styles.header}>Бонус {quota.value}</div>;
    }, [quota.value]);

    const content = useMemo(
        () => (
            <Content isModal>
                {expiredAfterDays ? <BonusExpiresWarning expiredDays={expiredAfterDays} email={info.owner.email} /> : null}
                <SubscriptionInfoBlock size="large" title={InfoTitles.friendBonus} id={InfoTypes.friendBonus}>
                    <div className={styles.friendBonus}>
                        <FriendAvatar size={20} info={info} />
                        <Text>{info.owner.email}</Text>
                    </div>
                </SubscriptionInfoBlock>
                <SubscriptionInfoBlock size="large" title={InfoTitles.conditionBonus} id={InfoTypes.conditionBonus}>
                    <Text className={styles.text}>{description}</Text>
                </SubscriptionInfoBlock>
                <SubscriptionInfoBlock size="large" title={InfoTitles.startBonus} id={InfoTypes.startBonus}>
                    <Text className={styles.text}>{bonusStart}</Text>
                </SubscriptionInfoBlock>
                <SubscriptionInfoBlock size="large" title={InfoTitles.featuresBonus} id={InfoTypes.featuresBonus}>
                    <FeaturesList />
                </SubscriptionInfoBlock>
            </Content>
        ),
        [isMobile, description, bonusStart, expiredAfterDays, info]
    );

    const mobileContent = useMemo(
        () => (
            <div ref={scrollRef} className={styles.rootMobile}>
                <CommonInfo isReferral space={quota} />
                <Spacing size={24} />
                <div className={styles.container}>
                    {expiredAfterDays ? <BonusExpiresWarning isMobile expiredDays={expiredAfterDays} email={info.owner.email} /> : null}
                    <ReferralInfoItem title="Начислен за друга" description={info.owner.email} icon={<Icon20UserOutline />} />
                    <ReferralInfoItem title="Условия" description={description} icon={<Icon20InfoCircleOutline />} />
                    <ReferralInfoItem title="Бонус получен" description={bonusStart} icon={<Icon20CalendarOutline />} />
                </div>
                <Spacing size={21} />
                <div className={styles.rectangle} />
            </div>
        ),
        [isMobile, quota, info]
    );

    if (isMobile) {
        return (
            <MobileDialog
                id="referrer-user-modal-phone"
                onClose={handleClose}
                closeOnDimmerClick
                mod="confirm"
                topmost
                scrollRef={scrollRef}
                className={styles.rootMobile}
            >
                {mobileContent}
            </MobileDialog>
        );
    }
    return (
        <Dialog open header={header} id="referrer-user-modal" onCancel={handleClose} className={styles.root}>
            {content}
        </Dialog>
    );
}

/* tempexp_18086-end */
